import { useAppContext } from '../../contexts/AppContext';
import { useEffect } from 'react';
import { Server } from '../../services/Server';
import { useQuery } from '@tanstack/react-query';
import { Backdrop, Box, CircularProgress, Typography } from '@mui/material';
import SoilingRisk from './SoilingRisk';
import SoilingRatio from './SoilingRatio';
import { getYearlyDaysFromDate } from '../../utils/utils';
import GlobalSoilingIndex from './GlobalSoilingIndex';
import CardBox from '../common/CardBox';
import { handleError } from '../../utils/Error';
import toast from 'react-hot-toast';
import { InfoAlert } from '../common/InfoAlert';
import { useFormContext } from '../../contexts/FormContext';

export const dynamicText = {
  a: (
    <Typography color="text.primary" variant="h5" pb={2} mb={0}>
      It is advised to run the mitigation strategy process to evaluate cleaning solutions suitable for your project.
    </Typography>
  ),
  b: (
    <Typography color="text.primary" variant="h5" pb={2} mb={0}>
      Rain data indicates that you may experience more than 12 months without rainfall year (rain {'<'} 1.2 mm /day), which could lead to
      significant soiling accumulation.
    </Typography>
  ),
  c: (
    <Typography color="text.primary" variant="h5" pb={2} mb={0}>
      Relative humidity and wind speed data indicate that these weather conditions are conducive to rapid soiling accumulation. Therefore,
      soiling losses could be considerable during extended dry periods.
    </Typography>
  ),
  d: (
    <Typography color="text.primary" variant="h5" pb={2} mb={0}>
      Airborne particulate matter shows a high rate of particle accumulation. Therefore, soiling losses could be considerable during
      extended dry periods.
    </Typography>
  ),
  e: (
    <Typography color="text.primary" variant="h5" pb={2} mb={0}>
      Typical year indicates that there is never a month where total rain is below 30 mm/month, Preventing soiling accumulation.
    </Typography>
  ),
};

export const dynamicText0Switch = (data: any) => {
  const text = [];
  if (data?.scores?.global > 0.5) {
    text.push(dynamicText['a']);
  }
  if (data?.scores?.rain >= 0.9) {
    text.push(dynamicText['b']);
  }
  if (data?.scores?.deposition > 0.45 && data?.scores?.global < 0.55) {
    text.push(dynamicText['c']);
  }
  if (data?.scores?.soiling_level > 3 && data?.scores?.global < 0.55) {
    text.push(dynamicText['d']);
  }
  if (data?.scores?.deposition === 0 || data?.scores?.rain < 0.3) {
    text.push(dynamicText['e']);
  }
  return text.length ? <CardBox>{text}</CardBox> : '';
};

type Props = {
  name: string;
  setResults: any;
};

const AppSoilingAssessment = ({ name, setResults }: Props) => {
  const { setModified } = useAppContext();
  const { formData } = useFormContext();
  const { postSoilingAssessmentAPI } = Server();

  const {
    data: data = [],
    isLoading: isLoading,
    isError: isError,
    error: error,
  } = useQuery({
    // query key uniquely identifies the query and will invalidate when a parameter changes.
    queryKey: [
      'soiling-assessment-' +
        JSON.stringify({
          ...formData.location,
          yearly_cleaning_dates: getYearlyDaysFromDate(formData.yearly_cleaning_days),
          cleaning_efficiency: formData.cleaning_efficiency,
          compute_rain_efficiency: formData.rain?.compute_rain_efficiency ?? false,
          variable_tilt_angle: formData?.variable_tilt_angle,
          ...formData?.tilt_angle,
          surface_tilt: formData.surface_tilt,
          rain_efficiency: formData.rain.rain_efficiency,
          rain_threshold: formData?.rain_threshold,
          user_patterns: {
            ...(formData?.rain.from_api !== true && formData?.rain?.rain_pattern ? { rain_pattern: formData.rain.rain_pattern } : {}),
            ...(formData?.relative_humidity?.from_api !== true && formData?.relative_humidity?.pattern
              ? { relative_humidity_pattern: formData.relative_humidity.pattern }
              : {}),
            ...(formData?.wind_speed?.from_api !== true && formData?.wind_speed?.pattern
              ? { wind_speed_pattern: formData.wind_speed.pattern }
              : {}),
            ...(formData?.soiling_ratio?.from_api !== true && formData?.soiling_ratio?.pattern
              ? { soiling_ratio_pattern: formData.soiling_ratio.pattern }
              : {}),
          },
        }),
    ],
    queryFn: () =>
      postSoilingAssessmentAPI(
        {
          ...formData.location,
          yearly_cleaning_dates: getYearlyDaysFromDate(formData.yearly_cleaning_days),
          cleaning_efficiency: formData.cleaning_efficiency,
          compute_rain_efficiency: formData.rain?.compute_rain_efficiency ?? false,
          variable_tilt_angle: formData?.variable_tilt_angle,
          ...formData?.tilt_angle,
          surface_tilt: formData.surface_tilt,
          rain_efficiency: formData.rain.rain_efficiency,
          rain_threshold: formData?.rain_threshold,
        },
        {
          user_patterns: {
            ...(formData?.rain.from_api !== true && formData?.rain?.rain_pattern ? { rain_pattern: formData.rain.rain_pattern } : {}),
            ...(formData?.relative_humidity?.from_api !== true && formData?.relative_humidity?.pattern
              ? { relative_humidity_pattern: formData.relative_humidity.pattern }
              : {}),
            ...(formData?.wind_speed?.from_api !== true && formData?.wind_speed?.pattern
              ? { wind_speed_pattern: formData.wind_speed.pattern }
              : {}),
            ...(formData?.soiling_ratio?.from_api !== true && formData?.soiling_ratio?.pattern
              ? { soiling_ratio_pattern: formData.soiling_ratio.pattern }
              : {}),
          },
        },
      ),
  });

  if (isError) {
    //@ts-ignore
    if (error?.response?.data?.detail) {
      //@ts-ignore
      error.response.data.detail.forEach((elem: { msg: string; loc: string[] }) => toast.error(`${elem.msg}: ${elem.loc.join(' - ')}`));
    } else {
      handleError(error);
    }
  }

  // DRL: check for 3 the same calls
  // useEffect(() => {
  //   refetch();
  // }, [invalidate]);

  useEffect(() => {
    setResults((prevResults: any) => {
      return { ...prevResults, global_soiling_index: Math.round(data?.scores?.global * 10000) / 100 };
    });
    setModified(true);
  }, [data]);

  return (
    <>
      <Box sx={{ justifyContent: 'center', pl: 1, pr: 1 }}>
        {isLoading ? (
          <Backdrop open={true} sx={{ width: '100%', height: '450px', position: 'relative' }}>
            <CircularProgress />
          </Backdrop>
        ) : (
          Object.keys(data).length > 0 && (
            <>
              <InfoAlert severity="info">
                This page provides a comprehensive risk assessment for soiling buildup, based on the meteorological and soiling data defined
                in the previous steps.
              </InfoAlert>
              <Box sx={{ mt: 2 }}>
                <GlobalSoilingIndex data={data} />
              </Box>
              <Box>
                <SoilingRisk data={{ scores: data?.scores }} name={name} />
              </Box>
              <Box>
                <SoilingRatio
                  data={data?.soiling}
                  yearly_cleaning_days={formData?.yearly_cleaning_days}
                  rain_threshold={formData?.rain_threshold}
                  ymin={0.4}
                  ymax={1.2}
                />
              </Box>
            </>
          )
        )}
      </Box>
    </>
  );
};

export default AppSoilingAssessment;
